import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { movies } from '@/mocks/movies';
import MovieCard from '@/components/feature/MovieCard';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 12;

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q')?.trim() ?? '';
  const pageParam = Number(searchParams.get('page') ?? '1');
  const page = Number.isFinite(pageParam) && pageParam > 0 ? Math.floor(pageParam) : 1;
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  usePageMeta(
    query ? `“${query}”的搜索结果 - 青苹果影院` : '影片搜索 - 青苹果影院',
    query
      ? `青苹果影院为您找到与“${query}”相关的影片与剧集，可按结果页继续浏览详情与相关推荐。`
      : '在青苹果影院搜索电影与电视剧，支持按影片名称、类型、导演与主演关键词查找。',
    '青苹果影院,影片搜索,电影搜索,电视剧搜索',
  );

  const results = useMemo(() => {
    if (!query) return [];
    const lower = query.toLowerCase();
    return movies.filter((movie) => {
      const fields = [movie.title, movie.region, movie.director, ...movie.genres, ...movie.cast];
      return fields.some((field) => field.toLowerCase().includes(lower));
    });
  }, [query]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageResults = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = keyword.trim();
    if (!next) {
      setSearchParams(new URLSearchParams());
      return;
    }
    setSearchParams({ q: next });
  };

  const handlePageChange = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete('page');
    else next.set('page', `${nextPage}`);
    setSearchParams(next);
    window.scrollTo({ top: 260, behavior: 'smooth' });
  };

  const handleClear = () => {
    setKeyword('');
    setSearchParams(new URLSearchParams());
  };

  return (
    <div className="w-full">
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 h-11 flex items-center gap-2 text-xs text-foreground-500">
          <Link to="/" className="hover:text-primary-700 transition-colors">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm" aria-hidden="true" />
          <span className="text-foreground-800">搜索</span>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-4 md:px-8 pt-8 md:pt-10">
        <div className="rounded-lg border border-background-200 bg-background-100/70 p-4 md:p-7">
          <h1 className="text-2xl md:text-3xl font-semibold text-foreground-950 tracking-tight">搜索影片</h1>
          <p className="mt-2 text-sm text-foreground-600">支持影片名称、类型、导演与主演关键词匹配</p>

          <form onSubmit={handleSubmit} className="mt-5 flex flex-col sm:flex-row gap-3" role="search">
            <div className="flex-1 flex items-center gap-2 h-11 px-4 rounded-md border border-background-200 bg-background-50 focus-within:border-primary-400 transition-colors">
              <i className="ri-search-line text-lg text-foreground-500" aria-hidden="true" />
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="输入影片名称、类型或演员"
                aria-label="搜索关键词"
                className="flex-1 min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={() => setKeyword('')}
                  aria-label="清空关键词"
                  className="w-6 h-6 flex items-center justify-center text-foreground-400 hover:text-foreground-700 cursor-pointer"
                >
                  <i className="ri-close-circle-fill text-base" aria-hidden="true" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>

          {query && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs md:text-sm">
              <span className="text-foreground-500">当前关键词</span>
              <span className="px-2.5 py-1 rounded-md bg-background-50 border border-background-200 text-foreground-800">
                {query}
              </span>
              <button
                type="button"
                onClick={handleClear}
                className="ml-1 text-foreground-500 hover:text-accent-700 transition-colors cursor-pointer"
              >
                清除
              </button>
            </div>
          )}
        </div>

        <div className="mt-6 md:mt-8 flex items-center justify-between gap-3">
          <p className="text-sm text-foreground-600">
            {query ? (
              <>
                与“<strong className="text-foreground-950 font-medium">{query}</strong>”相关的作品共{' '}
                <strong className="text-primary-700 font-semibold">{results.length}</strong> 部
              </>
            ) : (
              <span className="text-foreground-500">输入关键词开始查找影片</span>
            )}
          </p>
          {query && results.length > 0 && (
            <p className="text-xs text-foreground-400 font-label">
              第 {currentPage} / {totalPages} 页
            </p>
          )}
        </div>

        <div className="mt-4 pb-14 md:pb-16">
          {!query ? (
            <EmptyState
              icon="ri-search-line"
              title="输入关键词开始搜索"
              description="可以试试搜索“星海”“悬疑”“长夜”等关键词，或直接浏览全部影片。"
              actionLabel="浏览全部影片"
              actionTo="/category"
            />
          ) : pageResults.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-3 gap-y-6 md:gap-x-4 md:gap-y-8">
                {pageResults.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} showCategory />
                ))}
              </div>
              <div className="mt-8 md:mt-10">
                <Pagination page={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
              </div>
            </>
          ) : (
            <EmptyState
              icon="ri-file-search-line"
              title="没有找到相关影片"
              description={`暂时没有与“${query}”匹配的作品，换个关键词试试，或回到分类页浏览。`}
              actionLabel="重置搜索"
              onAction={handleClear}
            />
          )}
        </div>
      </div>
    </div>
  );
}